import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  Sheet,
  Chip,
  IconButton,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormLabel,
  Select,
  Option,
  Textarea,
  Stack,
  Alert,
  LinearProgress,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  List,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Checkbox,
  CircularProgress,
  Divider,
} from '@mui/joy';
import {
  Plus as Add,
  Lock,
  LockOpen,
  CheckCircle,
  ClipboardText as Assignment,
  X as Close,
  ArrowsClockwise as Refresh,
} from '@phosphor-icons/react';
import { apiAccountingClient } from '../../lib/apiAccounting';
import type { 
  AccountingPeriod, 
  PeriodStatus,
  PeriodType,
  MonthEndChecklistItem,
  ChecklistItemType 
} from '../../types/accounting';

interface PeriodManagementProps {}

export const PeriodManagement: React.FC<PeriodManagementProps> = () => {
  const [periods, setPeriods] = useState<AccountingPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<AccountingPeriod | null>(null);
  const [checklist, setChecklist] = useState<MonthEndChecklistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createYear, setCreateYear] = useState<number>(new Date().getFullYear());
  const [createPeriodType, setCreatePeriodType] = useState<PeriodType>('MONTHLY');
  const [creatingPeriods, setCreatingPeriods] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeAction, setCloseAction] = useState<'soft_close' | 'hard_close' | 'lock' | 'reopen'>('soft_close');
  const [closeNotes, setCloseNotes] = useState('');
  const [selectedTab, setSelectedTab] = useState(0);

  // Load periods
  useEffect(() => {
    fetchPeriods();
  }, []);

  // Load checklist when period is selected
  useEffect(() => {
    if (selectedPeriod) {
      fetchChecklist(selectedPeriod.id);
    }
  }, [selectedPeriod]);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const response = await apiAccountingClient.getPeriods();
      const periodList = Array.isArray(response) ? response : (response as any).results || [];
      setPeriods(periodList);
      
      // Auto-select current period
      const currentPeriod = periodList.find((p: AccountingPeriod) => 
        p.status === 'OPEN' || p.status === 'SOFT_CLOSED'
      ) || periodList[0];
      if (currentPeriod) {
        setSelectedPeriod(currentPeriod);
      }
    } catch (err) {
      console.error('Failed to fetch periods:', err);
      setError('Failed to load accounting periods');
    } finally {
      setLoading(false);
    }
  };

  const fetchChecklist = async (periodId: number) => {
    try {
      const response = await apiAccountingClient.getPeriodChecklist(periodId);
      setChecklist(response);
    } catch (err) {
      console.error('Failed to fetch checklist:', err);
    }
  };

  const handleCreatePeriods = async (year: number, periodType: PeriodType) => {
    try {
      setCreatingPeriods(true);
      await apiAccountingClient.createYearPeriods({ year, period_type: periodType });
      await fetchPeriods();
      setShowCreateModal(false);
      toast.success(`Created ${year} ${periodType.toLowerCase()} periods`);
    } catch (err) {
      console.error('Failed to create periods:', err);
      toast.error('Failed to create periods. Please try again.');
    } finally {
      setCreatingPeriods(false);
    }
  };

  const handlePeriodClose = async () => {
    if (!selectedPeriod) return;

    try {
      await apiAccountingClient.closePeriod(selectedPeriod.id, {
        action: closeAction,
        notes: closeNotes,
      });
      
      await fetchPeriods();
      setShowCloseModal(false);
      setCloseNotes('');
    } catch (err) {
      console.error('Failed to close period:', err);
      alert('Failed to perform period action. Please try again.');
    }
  };

  const handleChecklistItemToggle = async (item: MonthEndChecklistItem) => {
    try {
      if (item.is_completed) {
        await apiAccountingClient.uncompleteChecklistItem(item.id);
      } else {
        await apiAccountingClient.completeChecklistItem(item.id, { notes: '' });
      }
      
      await fetchChecklist(selectedPeriod!.id);
    } catch (err) {
      console.error('Failed to update checklist item:', err);
    }
  };

  const getStatusColor = (status: PeriodStatus): 'primary' | 'success' | 'warning' | 'danger' | 'neutral' => {
    switch (status) {
      case 'FUTURE':
        return 'primary';
      case 'OPEN':
        return 'success';
      case 'SOFT_CLOSED':
        return 'warning';
      case 'HARD_CLOSED':
        return 'danger';
      case 'LOCKED':
        return 'neutral';
      default:
        return 'primary';
    }
  };

  const getStatusIcon = (status: PeriodStatus) => {
    switch (status) {
      case 'LOCKED':
        return <Lock />;
      case 'HARD_CLOSED':
      case 'SOFT_CLOSED':
        return <CheckCircle />;
      default:
        return <LockOpen />;
    }
  };

  const getAvailableActions = (period: AccountingPeriod | null) => {
    if (!period) return [];
    
    const actions = [];
    
    switch (period.status) {
      case 'OPEN':
        actions.push({ value: 'soft_close', label: 'Soft Close Period' });
        break;
      case 'SOFT_CLOSED':
        actions.push({ value: 'hard_close', label: 'Hard Close Period' });
        actions.push({ value: 'reopen', label: 'Reopen Period' });
        break;
      case 'HARD_CLOSED':
        actions.push({ value: 'lock', label: 'Lock Period' });
        actions.push({ value: 'reopen', label: 'Reopen Period' });
        break;
      case 'LOCKED':
        // No actions available for locked periods
        break;
      case 'FUTURE':
        // No actions available for future periods
        break;
    }
    
    return actions;
  };

  const groupChecklistByType = (items: MonthEndChecklistItem[]) => {
    const grouped: Record<ChecklistItemType, MonthEndChecklistItem[]> = {
      RECONCILIATION: [],
      ACCRUAL: [],
      ADJUSTMENT: [],
      REVIEW: [],
      APPROVAL: [],
      REPORT: [],
      OTHER: [],
    };

    items.forEach(item => {
      if (grouped[item.item_type]) {
        grouped[item.item_type].push(item);
      }
    });

    return grouped;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert color="danger">{error}</Alert>;
  }

  const checklistProgress = selectedPeriod?.checklist_progress || { total: 0, completed: 0, percentage: 0 };
  const groupedChecklist = groupChecklistByType(checklist);
  const availableActions = getAvailableActions(selectedPeriod);

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Typography level="h2" sx={{ fontSize: { xs: '1.5rem', md: undefined } }}>Period Management</Typography>
        <Button
          startDecorator={<Add />}
          onClick={() => setShowCreateModal(true)}
        >
          Create Year Periods
        </Button>
      </Box>

      <Tabs value={selectedTab} onChange={(event, value) => setSelectedTab(value as number)}>
        <TabList sx={{ flexWrap: 'wrap', '& > *': { flex: { xs: 1, sm: '0 0 auto' } } }}>
          <Tab>Periods Overview</Tab>
          <Tab>Month-End Checklist</Tab>
        </TabList>

        <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
          <Card>
            <CardContent>
              <Sheet sx={{ overflow: 'auto' }}>
                <Table stickyHeader sx={{ minWidth: 880 }}>
                  <thead>
                    <tr>
                      <th>Period</th>
                      <th>Type</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                      <th>Status</th>
                      <th>Checklist</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {periods.map((period) => (
                      <tr 
                        key={period.id}
                        onClick={() => setSelectedPeriod(period)}
                        style={{ 
                          cursor: 'pointer',
                          backgroundColor: selectedPeriod?.id === period.id ? 'var(--joy-palette-background-level1)' : undefined
                        }}
                      >
                        <td>
                          <Typography level="body-md" fontWeight="md">
                            {period.name}
                          </Typography>
                        </td>
                        <td>{period.period_type_display}</td>
                        <td>{new Date(period.start_date).toLocaleDateString()}</td>
                        <td>{new Date(period.end_date).toLocaleDateString()}</td>
                        <td>
                          <Chip
                            size="sm"
                            color={getStatusColor(period.status)}
                            startDecorator={getStatusIcon(period.status)}
                          >
                            {period.status_display}
                          </Chip>
                        </td>
                        <td>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              determinate
                              value={period.checklist_progress?.percentage || 0}
                              size="sm"
                              sx={{ width: 60 }}
                            />
                            <Typography level="body-xs">
                              {period.checklist_progress?.completed || 0}/{period.checklist_progress?.total || 0}
                            </Typography>
                          </Box>
                        </td>
                        <td>
                          <IconButton
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPeriod(period);
                              setShowCloseModal(true);
                            }}
                            disabled={period.status === 'LOCKED'}
                          >
                            <Assignment />
                          </IconButton>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
          {selectedPeriod && (
            <Card>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Typography level="h3">{selectedPeriod.name} Checklist</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                    <LinearProgress
                      determinate
                      value={checklistProgress.percentage}
                      sx={{ flexGrow: 1 }}
                    />
                    <Typography level="body-md">
                      {checklistProgress.completed} of {checklistProgress.total} completed
                    </Typography>
                    <Button
                      size="sm"
                      variant="soft"
                      startDecorator={<Refresh />}
                      onClick={() => fetchChecklist(selectedPeriod.id)}
                    >
                      Refresh
                    </Button>
                  </Box>
                </Box>

                <Divider />

                {Object.entries(groupedChecklist).map(([type, items]) => {
                  if (items.length === 0) return null;

                  return (
                    <Box key={type} sx={{ mt: 3 }}>
                      <Typography level="title-md" sx={{ mb: 2 }}>
                        {type.replace('_', ' ')}
                      </Typography>
                      <List size="sm">
                        {items.map((item) => (
                          <ListItem key={item.id}>
                            <ListItemDecorator>
                              <Checkbox
                                checked={item.is_completed}
                                onChange={() => handleChecklistItemToggle(item)}
                                disabled={selectedPeriod.status === 'LOCKED'}
                              />
                            </ListItemDecorator>
                            <ListItemContent>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    textDecoration: item.is_completed ? 'line-through' : undefined,
                                    opacity: item.is_completed ? 0.7 : 1,
                                  }}
                                >
                                  {item.description}
                                </Typography>
                                {item.is_required && (
                                  <Chip size="sm" variant="soft" color="warning">
                                    Required
                                  </Chip>
                                )}
                              </Box>
                              {item.completed_date && (
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                  Completed on {new Date(item.completed_date).toLocaleString()} by {item.completed_by_name}
                                </Typography>
                              )}
                            </ListItemContent>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  );
                })}
              </CardContent>
            </Card>
          )}
        </TabPanel>
      </Tabs>

      {/* Create Periods Modal */}
      <Modal open={showCreateModal} onClose={() => setShowCreateModal(false)}>
        <ModalDialog>
          <DialogTitle>
            <Typography level="h4">Create Accounting Periods</Typography>
            <IconButton 
              size="sm" 
              variant="plain"
              onClick={() => setShowCreateModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Year</FormLabel>
                <Select
                  value={createYear}
                  onChange={(_, value) => value != null && setCreateYear(value)}
                >
                  {[2024, 2025, 2026].map(year => (
                    <Option key={year} value={year}>{year}</Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Period Type</FormLabel>
                <Select
                  value={createPeriodType}
                  onChange={(_, value) => value != null && setCreatePeriodType(value as PeriodType)}
                >
                  <Option value="MONTHLY">Monthly</Option>
                  <Option value="QUARTERLY">Quarterly</Option>
                  <Option value="YEARLY">Yearly</Option>
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button
              loading={creatingPeriods}
              onClick={() => handleCreatePeriods(createYear, createPeriodType)}
            >
              Create Periods
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>

      {/* Period Close Modal */}
      <Modal open={showCloseModal} onClose={() => setShowCloseModal(false)}>
        <ModalDialog>
          <DialogTitle>
            <Typography level="h4">Period Action</Typography>
            <IconButton 
              size="sm" 
              variant="plain"
              onClick={() => setShowCloseModal(false)}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <Close />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Action</FormLabel>
                <Select
                  value={closeAction}
                  onChange={(e, value) => setCloseAction(value as any)}
                >
                  {availableActions.map(action => (
                    <Option key={action.value} value={action.value}>
                      {action.label}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  minRows={3}
                  value={closeNotes}
                  onChange={(e) => setCloseNotes(e.target.value)}
                  placeholder="Enter any notes about this action..."
                />
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" onClick={() => setShowCloseModal(false)}>
              Cancel
            </Button>
            <Button 
              color={closeAction === 'reopen' ? 'warning' : 'primary'}
              onClick={handlePeriodClose}
            >
              Confirm
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </Box>
  );
};